import React, { useEffect, useMemo, useState } from "react";
const defaultPattern = ["LOW", "LOW", "CARB", "LOW", "CARB", "LOW", "CARB"];
const mealKeys = ["Breakfast", "Snack 1", "Lunch", "Snack 2", "Dinner"];
const STORAGE_KEY = "carbCycler.v1";

function makeDefaultDay(type) {
  return {
    type,
    meals: mealKeys.reduce((acc, k) => ({ ...acc, [k]: false }), {}),
    waterOz: 0,
    workoutDone: false,
  };
}
function makeDefaultState() {
  return {
    waterTargetOz: 100,
    days: defaultPattern.map((t) => makeDefaultDay(t)),
  };
}

export default function App() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : makeDefaultState();
    } catch {
      return makeDefaultState();
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const totalWater = useMemo(
    () => state.days.reduce((s, d) => s + d.waterOz, 0),
    [state.days]
  );

  const resetWeek = () => setState(makeDefaultState());
  const setDay = (idx, patch) => {
    setState((prev) => {
      const days = [...prev.days];
      days[idx] = { ...days[idx], ...patch };
      return { ...prev, days };
    });
  };
  const toggleMeal = (idx, mk) => {
    const d = state.days[idx];
    setDay(idx, { meals: { ...d.meals, [mk]: !d.meals[mk] } });
  };
  const adjustWater = (idx, delta) => {
    const next = Math.max(0, Math.min(384, state.days[idx].waterOz + delta));
    setDay(idx, { waterOz: next });
  };

  return (
    <div className="app">
      <header className="topbar">
        <div>
          <h1>Carb-Cycler – 7-Day Checklist</h1>
          <p className="muted">
            VShred-inspired rotation • 4 Low-Carb & 3 Carb days • Hot yoga friendly
          </p>
        </div>
        <div className="controls">
          <label className="muted">Water Target</label>
          <input
            type="number"
            value={state.waterTargetOz}
            onChange={(e) =>
              setState({
                ...state,
                waterTargetOz: Math.max(60, Math.min(150, Number(e.target.value) || 0)),
              })
            }
          />
          <span className="muted">oz/day</span>
          <button className="btn dark" onClick={resetWeek}>Reset Week</button>
        </div>
      </header>

      <section className="grid">
        {state.days.map((day, i) => (
          <div key={i} className="card">
            <div className="row">
              <h2>Day {i + 1}</h2>
              <select
                value={day.type}
                onChange={(e) => setDay(i, { type: e.target.value })}
                className={day.type === "LOW" ? "tag low" : "tag carb"}
              >
                <option value="LOW">Low-Carb</option>
                <option value="CARB">Carb Day</option>
              </select>
            </div>

            <div className="stack">
              {mealKeys.map((mk) => (
                <label key={mk} className="row item">
                  <span>{mk}</span>
                  <input
                    type="checkbox"
                    checked={day.meals[mk]}
                    onChange={() => toggleMeal(i, mk)}
                  />
                </label>
              ))}
            </div>

            <div className="water">
              <div className="row">
                <span className="bold">Water</span>
                <span className="muted">
                  {day.waterOz} / {state.waterTargetOz} oz
                </span>
              </div>
              <div className="buttons">
                {[8, 16, 24, -8].map((d) => (
                  <button key={d} onClick={() => adjustWater(i, d)} className="btn">
                    {d > 0 ? `+${d} oz` : `${d} oz`}
                  </button>
                ))}
                <button onClick={() => setDay(i, { waterOz: 0 })} className="btn danger">
                  Clear
                </button>
              </div>
              <div className="bar">
                <div
                  className="fill"
                  style={{
                    width: `${Math.min(100, (day.waterOz / state.waterTargetOz) * 100)}%`,
                  }}
                />
              </div>
            </div>

            <label className="row item">
              <span>Workout complete</span>
              <input
                type="checkbox"
                checked={day.workoutDone}
                onChange={(e) => setDay(i, { workoutDone: e.target.checked })}
              />
            </label>
          </div>
        ))}
      </section>

      <section className="card">
        <h3>Week Summary</h3>
        <div className="summary">
          <div className="pill">
            <div className="muted">Low-Carb Days</div>
            <div className="big">
              {state.days.filter((d) => d.type === "LOW").length}
            </div>
          </div>
          <div className="pill">
            <div className="muted">Carb Days</div>
            <div className="big">
              {state.days.filter((d) => d.type === "CARB").length}
            </div>
          </div>
          <div className="pill">
            <div className="muted">Total Water</div>
            <div className="big">{totalWater} oz</div>
          </div>
          <div className="pill">
            <div className="muted">Workouts</div>
            <div className="big">
              {state.days.filter((d) => d.workoutDone).length} / 7
            </div>
          </div>
        </div>
      </section>

      <section className="grid two">
        <Card title="Free Foods (Unlimited)">
          <ul>
            <li>Leafy greens: spinach, kale, lettuce, arugula, Swiss chard, beet & collard greens, cabbage, microgreens, sprouts</li>
            <li>Flavor boosters: lemon/lime, vinegars, mustard, herbs & spices, hot sauce</li>
            <li>Very-low-cal veggies: zucchini, cucumber, celery, radishes, mushrooms, asparagus, green beans, peppers (moderate)</li>
            <li>Hydration: water, herbal tea, black coffee, infused water</li>
          </ul>
        </Card>

        <Card title="Swap List (Macro-Equivalent)">
          <p><b>Proteins</b> (≈20–25 g): Chicken/Turkey 4 oz • White fish 4 oz • Salmon 3 oz • Shrimp 5 oz • 93% lean ground 4 oz • 96% beef 4 oz • Eggs 3 • Egg whites 7 • Greek yogurt 6 oz • Cottage cheese 1/2 cup • Vegan protein 1 scoop</p>
          <p><b>Carbs</b> (≈20–25 g): Oatmeal 1/2 cup cooked • Quinoa 1/2 cup • Brown rice 1/2 cup • Sweet/White potato 100 g • Lentils/Chickpeas/Black beans 1/2 cup • Fruit: 1 cup berries / 1 small apple / 1/2 banana</p>
          <p><b>Fats</b> (≈100–120 cal): Avocado 1/2 • Olive oil 1 tsp • Almonds 12 • Walnuts 7 halves • Nut butter 1 tbsp • Flax/Chia 1 tbsp • Olives 10</p>
        </Card>
      </section>

      <footer className="muted small">
        Built as a starter • Data is stored locally in your browser.
      </footer>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      <div>{children}</div>
    </div>
  );
}
